<template>
  <div>
    <div class="flex mb-auto flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div class="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 class="mt-6 text-center text-3xl font-medium tracking-tight text-gray-900">
          {{ lang.panelHeadline }}
        </h2>
      </div>

      <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form
            ref="registrationForm"
            @submit.prevent="submitRegistrationForm"
            class="space-y-6"
            method="POST"
            :action="formAction"
          >
            <!-- Private || Company -->
            <div class="flex space-x-6">
              <input-radio
                id="is_private"
                name="company_private"
                v-model="companyPrivate"
                input-value="2"
                :label="lang.privateAccount"
              />

              <input-radio
                id="is_company"
                name="company_private"
                v-model="companyPrivate"
                input-value="1"
                :label="lang.businessAccount"
              />
            </div>

            <!-- Salutation -->
            <div class="flex space-x-6">
              <input-radio
                id="salutation_mr"
                name="salutation"
                :input-value="lang.mr"
                :label="lang.mr"
                v-model="salutation"
              />

              <input-radio
                id="salutation_mrs"
                name="salutation"
                :input-value="lang.mrs"
                :label="lang.mrs"
                v-model="salutation"
              />
            </div>
            <span v-if="errors.salutation" class="text-sm text-red-600">
              {{ errors.salutation[0] }}
            </span>

            <!-- Company -->
            <div class="mt-1" v-show="showCompanyFields" v-cloak>
              <input-text
                id="company"
                name="company"
                autocomplete="company"
                :required="showCompanyFields"
                type="text"
                :label="lang.company"
                v-model="company"
                :error="!!errors.company"
              />
              <span v-if="errors.company" class="text-sm text-red-600">
                {{ errors.company[0] }}
              </span>
            </div>

            <!-- First Name -->
            <div class="mt-1">
              <input-text
                id="vorname"
                name="vorname"
                autocomplete="first_name"
                :required="true"
                type="text"
                :label="lang.firstName"
                v-model="vorname"
                :error="!!errors.vorname"
              />

              <!-- Honeypot field for bots - temporarily disabled, causing issues for real users -->
              <!--
              <div class="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true">
                <input-text
                  id="first_name"
                  name="first_name"
                  autocomplete="off"
                  type="text"
                  :label="lang.firstName"
                  v-model="firstName"
                />
              </div>
              -->

              <span v-if="errors.vorname" class="text-sm text-red-600">
                {{ errors.vorname[0] }}
              </span>
            </div>

            <!-- Last Name -->
            <div class="mt-1">
              <input-text
                id="last_name"
                name="last_name"
                autocomplete="last_name"
                :required="true"
                type="text"
                :label="lang.lastName"
                v-model="lastName"
                :error="!!errors.last_name"
              />
              <span v-if="errors.last_name" class="text-sm text-red-600">
                {{ errors.last_name[0] }}
              </span>
            </div>

            <!-- Email -->
            <div class="mt-1">
              <input-text
                id="email"
                name="email"
                autocomplete="email"
                :required="true"
                type="email"
                :label="lang.email"
                v-model="email"
                :error="!!errors.email"
              />
              <span v-if="errors.email" class="text-sm text-red-600">
                {{ errors.email[0] }}
              </span>
            </div>

            <!-- Password -->
            <div class="mt-1">
              <input-text
                id="password"
                name="password"
                autocomplete="new-password"
                :required="true"
                type="password"
                :label="lang.password"
                v-model="password"
                :error="!!errors.password"
              />
              <span v-if="errors.password" class="text-sm text-red-600">
                {{ errors.password[0] }}
              </span>
            </div>

            <!-- Password Confirmation -->
            <div class="mt-1">
              <input-text
                id="password_confirmation"
                name="password_confirmation"
                autocomplete="new-password"
                :required="true"
                type="password"
                :label="lang.repeatPassword"
                v-model="passwordConfirmation"
                :error="!!errors.password_confirmation"
              />
              <span v-if="errors.password_confirmation" class="text-sm text-red-600">
                {{ errors.password_confirmation[0] }}
              </span>
            </div>

            <!-- Newsletter -->
            <input-checkbox
              id="newsletter"
              name="newsletter"
              input-value="1"
              v-model="newsletter"
            >{{ lang.registerReceiveNewsletter }}</input-checkbox>

            <!-- T&C -->
            <input-checkbox
              id="tnc"
              name="tnc"
              :required="true"
              input-value="1"
              v-model="tnc"
              :error="!!errors.tnc"
            ><span v-html="lang.registerAcceptTnc"></span></input-checkbox>
            <span v-if="errors.tnc" class="text-sm text-red-600">
              {{ errors.tnc[0] }}
            </span>

            <div>
              <input type="hidden" :name="csrfName" :value="csrfToken">
              <input type="hidden" id="recaptchaToken" name="recaptchaToken" v-model="recaptchaToken">

              <plain-button
                button-type="submit"
                color="primary"
                :w-full="true"
              >{{ lang.registerFree }}</plain-button>
            </div>
          </form>

          <div class="mt-4">
            <h4 v-html="lang.alreadyAMember"></h4>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import InputText from '../inputs/InputText.vue';
import InputRadio from '../inputs/InputRadio.vue';
import InputCheckbox from '../inputs/InputCheckbox.vue';
import PlainButton from '../buttons/PlainButton.vue';

const props = defineProps({
  initCompanyPrivate: {
    type: [Number, String],
    default: 2, // company: 1, private: 2
  },
  recaptchaSiteKey: {
    type: String,
    default: '',
  },
  formAction: {
    type: String,
    required: true,
  },
  csrfToken: {
    type: String,
    required: true,
  },
  csrfName: {
    type: String,
    default: '_token',
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
  old: {
    type: Object,
    default: () => ({}),
  },
  lang: {
    type: Object,
    required: true,
  },
  initSalutation: {
    type: String,
    default: '',
  },
});

const registrationForm = ref(null);
const companyPrivate = ref(String(props.initCompanyPrivate));
const recaptchaToken = ref('');

// Form fields
const salutation = ref(props.initSalutation || props.lang.mr);
const company = ref(props.old.company || '');
const vorname = ref(props.old.vorname || '');
// Honeypot field temporarily disabled, causing issues for real users
// const firstName = ref(props.old.first_name || ''); // honeypot
const lastName = ref(props.old.last_name || '');
const email = ref(props.old.email || '');
const password = ref('');
const passwordConfirmation = ref('');
const newsletter = ref(props.old.newsletter || '0');
const tnc = ref(props.old.tnc || '0');

const showCompanyFields = computed(() => {
  return companyPrivate.value === '1' || companyPrivate.value === 1;
});

const loadScript = (url, callback) => {
  let script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = url;
  script.onload = () => callback();
  document.head.appendChild(script);
};

const submitRegistrationForm = () => {
  const siteKey = props.recaptchaSiteKey;
  window.grecaptcha.ready(() => {
    window.grecaptcha
      .execute(siteKey, { action: 'submit' })
      .then((token) => {
        recaptchaToken.value = token;
        setTimeout(() => {
          registrationForm.value.submit();
        }, 200);
      });
  });
};

onMounted(() => {
  if (props.recaptchaSiteKey) {
    loadScript(
      `https://www.google.com/recaptcha/api.js?render=${props.recaptchaSiteKey}`,
      () => {
        console.log('reCAPTCHA script loaded');
      }
    );
  }
});
</script>
